import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { FaPrint, FaDownload, FaSpinner } from 'react-icons/fa';
import { jobCardService } from '../../services/apiService';
import html2pdf from 'html2pdf.js';

// Map backend JobCard.status to high-level business stage
const getWorkflowStage = (status) => {
  switch (status) {
    case 'Created':
      return 'INTAKE / DIAGNOSIS';
    case 'PendingCustomerApproval':
      return 'QUOTATION & CUSTOMER APPROVAL';
    case 'CustomerApproved':
    case 'PartsOrdered':
      return 'IN_PROGRESS (Repair Prep)';
    case 'InProgress':
      return 'IN_PROGRESS (Repair Work)';
    case 'ReadyForQC':
    case 'QCRejected':
    case 'QCApproved':
      return 'QUALITY_CHECK';
    case 'Billed':
      return 'INVOICE & PAYMENT';
    case 'Paid':
      return 'READY FOR DELIVERY';
    case 'Delivered':
      return 'DELIVERED';
    case 'Closed':
      return 'CLOSED';
    case 'CustomerRejected':
      return 'CANCELLED (Customer Rejected)';
    case 'Cancelled':
      return 'CANCELLED';
    default:
      return status || 'UNKNOWN';
  }
};

const VehicleJobCard = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [formData, setFormData] = useState({
    jobNo: '',
    jobDate: new Date().toISOString().split('T')[0],
    carRegistrationNo: '',
    year: '',
    mileage: '',
    dateTimeIn: '',
    dateTimeOut: '',
    carMake: '',
    carModel: '',
    customerName: '',
    customerContactNo: '',
    engineNo: '',
    vinNo: '',
    reportedDefect: '',
    completedAction: '',
    parts: [],
    labour: 0,
    subTotal: 0,
    vat: 0,
    total: 0,
    technicianName: '',
    technicianSignature: '',
    technicianDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    let mounted = true;

    const hydrate = (foundJob) => {
      setJob(foundJob);
      setFormData((prev) => ({
        ...prev,
        jobNo: (foundJob.jobCardId || foundJob._id || '').toString().slice(-8),
        jobDate: foundJob.createdAt ? new Date(foundJob.createdAt).toISOString().split('T')[0] : prev.jobDate,
        carRegistrationNo: foundJob.vehicle?.registrationNumber || '',
        year: foundJob.vehicle?.year || '',
        mileage: foundJob.vehicle?.kmReading || foundJob.mileage || '',
        dateTimeIn: foundJob.garageIntake?.scheduledTime
          ? new Date(foundJob.garageIntake.scheduledTime).toLocaleString()
          : (foundJob.preferredServiceDate ? new Date(foundJob.preferredServiceDate).toLocaleString() : ''),
        carMake: foundJob.vehicle?.make || '',
        carModel: `${foundJob.vehicle?.model || ''} ${foundJob.vehicle?.variant || ''}`.trim(),
        customerName: foundJob.customerId?.name || '',
        customerContactNo: foundJob.customerId?.phone || '',
        engineNo: foundJob.vehicle?.engine || '',
        vinNo: foundJob.vehicle?.vin || '',
        reportedDefect: foundJob.complaintDescription || '',
        completedAction: foundJob.completedAction || '',
        parts: (foundJob.estimate?.parts || []).map(part => ({
          partNo: part.sku || part.partNo || '',
          partDescription: part.name || part.partDescription || '',
          qty: part.quantity || part.qty || 1,
          unitPrice: part.price || part.selectedOption?.price || part.unitPrice || 0,
          lineTotal: (part.quantity || part.qty || 1) * (part.price || part.selectedOption?.price || 0),
          labour: part.labour || 0
        })),
        labour: (foundJob.estimate?.laborItems || []).reduce((sum, li) => sum + (Number(li.hours) || 0) * (Number(li.rate) || 0), 0),
        subTotal: (() => {
          const partsTotal = (foundJob.estimate?.parts || []).reduce((sum, part) => {
            const qty = part.quantity || part.qty || 1;
            const price = part.price || part.selectedOption?.price || 0;
            return sum + (qty * price);
          }, 0);
          const perPartLabour = (foundJob.estimate?.parts || []).reduce((sum, part) => sum + (part.labour || 0), 0);
          const totalLabour = (foundJob.estimate?.laborItems || []).reduce((sum, li) => sum + (Number(li.hours) || 0) * (Number(li.rate) || 0), 0);
          return partsTotal + totalLabour + perPartLabour;
        })(),
        vat: (() => {
          const partsTotal = (foundJob.estimate?.parts || []).reduce((sum, part) => {
            const qty = part.quantity || part.qty || 1;
            const price = part.price || part.selectedOption?.price || 0;
            return sum + (qty * price);
          }, 0);
          const perPartLabour = (foundJob.estimate?.parts || []).reduce((sum, part) => sum + (part.labour || 0), 0);
          const totalLabour = (foundJob.estimate?.laborItems || []).reduce((sum, li) => sum + (Number(li.hours) || 0) * (Number(li.rate) || 0), 0);
          const subTotal = partsTotal + totalLabour + perPartLabour;
          return subTotal * 0.18;
        })(),
        total: (() => {
          const partsTotal = (foundJob.estimate?.parts || []).reduce((sum, part) => {
            const qty = part.quantity || part.qty || 1;
            const price = part.price || part.selectedOption?.price || 0;
            return sum + (qty * price);
          }, 0);
          const perPartLabour = (foundJob.estimate?.parts || []).reduce((sum, part) => sum + (part.labour || 0), 0);
          const totalLabour = (foundJob.estimate?.laborItems || []).reduce((sum, li) => sum + (Number(li.hours) || 0) * (Number(li.rate) || 0), 0);
          const subTotal = partsTotal + totalLabour + perPartLabour;
          return subTotal * 1.18;
        })(),
        technicianName: user?.name || ''
      }));
      setLastUpdatedAt(new Date());
    };

    const fetchJob = async (opts = { showSpinner: false }) => {
      try {
        if (opts.showSpinner) setRefreshing(true);
        const foundJob = await jobCardService.getById(jobId);
        if (!mounted) return;
        hydrate(foundJob);
      } catch (e) {
        if (mounted) setJob(null);
      } finally {
        if (mounted) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    setLoading(true);
    fetchJob();
    const interval = setInterval(() => fetchJob(), 12000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [jobId, user?.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Calculate totals when labour changes
      if (name === 'labour') {
        const partsTotal = updated.parts.reduce((sum, part) => {
          return sum + (parseFloat(part.lineTotal || 0) || 0);
        }, 0);
        const perPartLabour = updated.parts.reduce((sum, part) => {
          return sum + (parseFloat(part.labour || 0) || 0);
        }, 0);
        const labour = parseFloat(updated.labour) || 0;
        updated.subTotal = partsTotal + labour + perPartLabour;
        updated.vat = updated.subTotal * 0.18; // 18% VAT
        updated.total = updated.subTotal + updated.vat;
      }
      
      return updated;
    });
  };

  const handlePartChange = (index, field, value) => {
    setFormData(prev => {
      const updatedParts = [...prev.parts];
      const part = updatedParts[index] || {};
      
      // Update the field
      updatedParts[index] = { 
        ...part, 
        [field]: field === 'qty' || field === 'unitPrice' || field === 'labour' 
          ? parseFloat(value) || 0 
          : value
      };
      
      // Recalculate line total when qty or unitPrice changes
      if (field === 'qty' || field === 'unitPrice') {
        const qty = parseFloat(updatedParts[index].qty || 1) || 1;
        const unitPrice = parseFloat(updatedParts[index].unitPrice || 0) || 0;
        updatedParts[index].lineTotal = qty * unitPrice;
      }
      
      // Calculate totals (include per-part labour)
      const partsTotal = updatedParts.reduce((sum, part) => {
        return sum + (parseFloat(part.lineTotal || 0) || 0);
      }, 0);
      const perPartLabour = updatedParts.reduce((sum, part) => {
        return sum + (parseFloat(part.labour || 0) || 0);
      }, 0);
      const totalLabour = parseFloat(prev.labour) || 0;
      const subTotal = partsTotal + totalLabour + perPartLabour;
      const vat = subTotal * 0.18;
      const total = subTotal + vat;
      
      return {
        ...prev,
        parts: updatedParts,
        subTotal,
        vat,
        total
      };
    });
  };

  const addPartRow = () => {
    setFormData(prev => ({
      ...prev,
      parts: [...prev.parts, {
        partNo: '',
        partDescription: '',
        qty: 1,
        unitPrice: 0,
        lineTotal: 0,
        labour: 0
      }]
    }));
  };

  const handleLabourChange = (e) => {
    const labour = parseFloat(e.target.value) || 0;
    setFormData(prev => {
      const partsTotal = prev.parts.reduce((sum, part) => {
        return sum + (parseFloat(part.lineTotal || 0) || 0);
      }, 0);
      const perPartLabour = prev.parts.reduce((sum, part) => {
        return sum + (parseFloat(part.labour || 0) || 0);
      }, 0);
      const subTotal = partsTotal + labour + perPartLabour;
      const vat = subTotal * 0.18;
      const total = subTotal + vat;
      return {
        ...prev,
        labour,
        subTotal,
        vat,
        total
      };
    });
  };

  // const removePartRow = (index) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     parts: prev.parts.filter((_, i) => i !== index)
  //   }));
  // };

  const handleDownloadPdf = () => {
    const content = document.getElementById('jobcard-print-content');
    if (!content) return;

    const clone = content.cloneNode(true);
    clone.querySelectorAll('.no-print').forEach((el) => el.remove());
    clone.querySelectorAll('input, textarea').forEach((el) => {
      el.setAttribute('value', el.value || '');
      if (el.tagName === 'TEXTAREA') el.textContent = el.value || '';
    });

    const options = {
      margin: 10,
      filename: `JobCard-${formData.jobNo || 'Print'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().from(clone).set(options).save();
  };

  const handlePrint = () => {
    const printContent = document.getElementById('jobcard-print-content');
    if (!printContent) {
      window.print();
      return;
    }
    // Clone and prepare for print
    const clone = printContent.cloneNode(true);
    clone.querySelectorAll('.no-print').forEach((el) => el.remove());
    // Ensure input/textarea values are in the serialized HTML (React controlled inputs)
    clone.querySelectorAll('input, textarea').forEach((el) => {
      el.setAttribute('value', el.value || '');
      if (el.tagName === 'TEXTAREA') el.textContent = el.value || '';
    });
    const html = clone.innerHTML;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print the job card.');
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Job Card - ${formData.jobNo || 'Print'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; font-size: 12px; color: #000; padding: 20px; background: #fff; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #000; padding: 6px 8px; text-align: left; }
            input, textarea {
              width: 100%;
              border: 2px solid #000;
              background: transparent;
              font-size: inherit;
              padding: 6px 8px;
              line-height: 1.4;
            }
            input:focus, textarea:focus { outline: none; }
            .bg-white { background: #fff; }
            .shadow-lg { box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); }
            .p-8 { padding: 2rem; }
            .text-center { text-align: center; }
            .mb-2 { margin-bottom: 0.5rem; }
            .mb-4 { margin-bottom: 1rem; }
            .mb-6 { margin-bottom: 1.5rem; }
            .mb-8 { margin-bottom: 2rem; }
            .mt-4 { margin-top: 1rem; }
            .mt-6 { margin-top: 1.5rem; }
            .mt-8 { margin-top: 2rem; }
            .text-4xl { font-size: 2.25rem; }
            .font-bold { font-weight: 700; }
            .text-gray-900 { color: #111827; }
            .text-sm { font-size: 0.875rem; }
            .font-semibold { font-weight: 600; }
            .space-y-4 > * + * { margin-top: 1rem; }
            .grid { display: grid; }
            .grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
            .grid-cols-5 { grid-template-columns: repeat(5, minmax(0, 1fr)); }
            .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .gap-4 { gap: 1rem; }
            .gap-6 { gap: 1.5rem; }
            .block { display: block; }
            .border-2 { border-width: 2px; }
            .border-black { border-color: #000; }
            .border { border-width: 1px; }
            .border-solid { border-style: solid; }
            .resize-none { resize: none; }
            .flex { display: flex; }
            .justify-end { justify-content: flex-end; }
            .justify-between { justify-content: space-between; }
            .items-center { align-items: center; }
            .w-64 { width: 16rem; }
            .w-24 { width: 6rem; }
            .w-48 { width: 12rem; }
            .h-32 { height: 8rem; }
            .h-24 { height: 6rem; }
            .h-8 { min-height: 2rem; }
            .bg-gray-100 { background: #f3f4f6; }
            .bg-gray-200 { background: #e5e7eb; }
            .rounded { border-radius: 0.25rem; }
            .relative { position: relative; }
            .absolute { position: absolute; }
            .text-xs { font-size: 0.75rem; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            .italic { font-style: italic; }
            .text-gray-500 { color: #6b7280; }
            .text-gray-600 { color: #4b5563; }
            .-left-8 { left: -2rem; }
            .-right-8 { right: -2rem; }
            .-top-4 { top: -1rem; }
            .top-1\\/2 { top: 50%; }
            .left-1\\/2 { left: 50%; }
            .bottom-0 { bottom: 0; }
            .-translate-y-1\\/2 { transform: translateY(-50%); }
            .-translate-x-1\\/2 { transform: translateX(-50%); }
            .-translate-y-4 { transform: translateY(-1rem); }
            .translate-y-4 { transform: translateY(1rem); }
            .translate-x-1\\/2 { transform: translateX(50%); }
            .print\\:shadow-none { box-shadow: none !important; }
            @media print { body { padding: 0; } .shadow-lg, .print\\:shadow-none { box-shadow: none !important; } }
          </style>
        </head>
        <body>
          ${html}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    }, 250);
  };

  // const handleBodyDamageMark = (area) => {
  //   // Toggle damage mark on body diagram
  //   setFormData(prev => ({
  //     ...prev,
  //     bodyDamageAreas: {
  //       ...prev.bodyDamageAreas,
  //       [area]: !prev.bodyDamageAreas?.[area]
  //     }
  //   }));
  // };

  if (loading) {
    return (
      <div className="checkout-light-page min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <FaSpinner className="animate-spin text-3xl text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading job card…</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="checkout-light-page min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Job not found</p>
          <button onClick={() => navigate('/service/booking')} className="btn-primary">
            Book New Service
          </button>
        </div>
      </div>
    );
  }

  const customerQcApprovedBySuperadmin =
    job?.qc?.approvedByRole === 'superadmin' ||
    (!job?.qc?.approvedByRole &&
      (job?.timeline || []).some((t) => t?.status === 'QCApproved' && t?.byRole === 'superadmin'));
  const displayStatus = (() => {
    if (job.status === 'ReadyForQC' || job.status === 'QCRejected') return 'InProgress';
    if (job.status === 'QCApproved') return customerQcApprovedBySuperadmin ? 'Billed' : 'InProgress';
    return job.status;
  })();

  const stage = getWorkflowStage(displayStatus);
  const allocationStatus = (job?.allocation?.status || 'pending').toString();
  const allocationReason = (job?.allocation?.reason || '').toString().trim();
  const intake = job?.garageIntake || {};

  return (
    <div className="checkout-light-page min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto w-full px-4">
        {/* Action Buttons */}
        <div className="mb-6 flex gap-4 justify-end print:hidden">
          <button onClick={handleDownloadPdf} className="btn-primary flex items-center gap-2">
            <FaDownload /> Download PDF
          </button>
          <button onClick={handlePrint} className="btn-secondary flex items-center gap-2">
            <FaPrint /> Print
          </button>
          <button onClick={() => navigate(`/service/job/${jobId}`)} className="btn-outline">
            Back to Job
          </button>
        </div>

        {/* Job Card Form - only this content is sent to print window */}
        <div id="jobcard-print-content" className="bg-white shadow-lg p-8 print:shadow-none">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">VEHICLE JOB CARD</h1>
            <p className="text-sm text-gray-700">
              Stage: <span className="font-semibold">{stage}</span>{' '}
              <span className="text-xs text-gray-400 font-mono">({displayStatus})</span>
            </p>
            <div className="mt-2 text-xs text-gray-500 flex items-center justify-center gap-2 print:hidden">
              <span>Allocation: <span className="font-mono text-gray-700">{allocationStatus}</span></span>
              {allocationStatus === 'reallocate' && (
                <span className="text-amber-800">Reason: {allocationReason || '—'}</span>
              )}
              <span>·</span>
              <span>Last updated: {lastUpdatedAt ? lastUpdatedAt.toLocaleTimeString() : '—'}</span>
              <button
                type="button"
                onClick={async () => {
                  setRefreshing(true);
                  try {
                    const foundJob = await jobCardService.getById(jobId);
                    setJob(foundJob);
                    setLastUpdatedAt(new Date());
                  } finally {
                    setRefreshing(false);
                  }
                }}
                className="ml-2 underline text-blue-700 hover:text-blue-900"
              >
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>

          {/* Top Section - Vehicle and Customer Details */}
          <div className="mb-6 space-y-4">
            {/* Row 1 */}
            <div className="grid grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Job No.</label>
                <input
                  type="text"
                  name="jobNo"
                  value={formData.jobNo}
                  onChange={handleInputChange}
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Job Date</label>
                <input
                  type="date"
                  name="jobDate"
                  value={formData.jobDate}
                  onChange={handleInputChange}
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Car Registration No.</label>
                <input
                  type="text"
                  name="carRegistrationNo"
                  value={formData.carRegistrationNo}
                  onChange={handleInputChange}
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Year</label>
                <input
                  type="text"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Mileage</label>
                <input
                  type="text"
                  name="mileage"
                  value={formData.mileage}
                  onChange={handleInputChange}
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Date/Time In</label>
                <input
                  type="text"
                  name="dateTimeIn"
                  value={formData.dateTimeIn}
                  onChange={handleInputChange}
                  placeholder="DD/MM/YYYY HH:MM"
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Date/Time Out</label>
                <input
                  type="text"
                  name="dateTimeOut"
                  value={formData.dateTimeOut}
                  onChange={handleInputChange}
                  placeholder="DD/MM/YYYY HH:MM"
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Car Make</label>
                <input
                  type="text"
                  name="carMake"
                  value={formData.carMake}
                  onChange={handleInputChange}
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Car Model</label>
                <input
                  type="text"
                  name="carModel"
                  value={formData.carModel}
                  onChange={handleInputChange}
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Customer Name</label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Customer Contact No.</label>
                <input
                  type="text"
                  name="customerContactNo"
                  value={formData.customerContactNo}
                  onChange={handleInputChange}
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Engine No.</label>
                <input
                  type="text"
                  name="engineNo"
                  value={formData.engineNo}
                  onChange={handleInputChange}
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">VIN No.</label>
                <input
                  type="text"
                  name="vinNo"
                  value={formData.vinNo}
                  onChange={handleInputChange}
                  className="w-full border-2 border-black px-2 py-1 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Middle Section */}
          <div className="grid grid-cols-1 gap-6 mb-6">
            {/* Reported Defect and Completed Action */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Reported Defect</label>
                <textarea
                  name="reportedDefect"
                  value={formData.reportedDefect}
                  onChange={handleInputChange}
                  rows={8}
                  className="w-full border-2 border-black px-2 py-1 text-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Completed Action</label>
                <textarea
                  name="completedAction"
                  value={formData.completedAction}
                  onChange={handleInputChange}
                  rows={8}
                  className="w-full border-2 border-black px-2 py-1 text-sm resize-none"
                />
              </div>
            </div>
          </div>

          {/* Parts and Labour Table */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Parts and Labour</h3>
            <div className="border-2 border-black">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border border-black px-2 py-1 text-left font-semibold">Part No.</th>
                    <th className="border border-black px-2 py-1 text-left font-semibold">Part Description</th>
                    <th className="border border-black px-2 py-1 text-center font-semibold">Qty</th>
                    <th className="border border-black px-2 py-1 text-right font-semibold">Unit Price</th>
                    <th className="border border-black px-2 py-1 text-right font-semibold">Line Total</th>
                    <th className="border border-black px-2 py-1 text-right font-semibold">Labour</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.parts.map((part, index) => (
                    <tr key={index}>
                      <td className="border border-black px-2 py-1">
                        <input
                          type="text"
                          value={part.partNo || part.sku || ''}
                          onChange={(e) => handlePartChange(index, 'partNo', e.target.value)}
                          className="w-full border-0 focus:outline-none text-sm"
                        />
                      </td>
                      <td className="border border-black px-2 py-1">
                        <input
                          type="text"
                          value={part.partDescription || part.name || ''}
                          onChange={(e) => handlePartChange(index, 'partDescription', e.target.value)}
                          className="w-full border-0 focus:outline-none text-sm"
                        />
                      </td>
                      <td className="border border-black px-2 py-1">
                        <input
                          type="number"
                          value={part.qty || part.quantity || 1}
                          onChange={(e) => handlePartChange(index, 'qty', e.target.value)}
                          className="w-full border-0 focus:outline-none text-sm text-center"
                          min="1"
                        />
                      </td>
                      <td className="border border-black px-2 py-1">
                        <input
                          type="number"
                          value={part.unitPrice || part.price || 0}
                          onChange={(e) => handlePartChange(index, 'unitPrice', e.target.value)}
                          className="w-full border-0 focus:outline-none text-sm text-right"
                          step="0.01"
                        />
                      </td>
                      <td className="border border-black px-2 py-1 text-right">
                        ₹{(part.lineTotal || 0).toFixed(2)}
                      </td>
                      <td className="border border-black px-2 py-1">
                        <input
                          type="number"
                          value={part.labour || 0}
                          onChange={(e) => handlePartChange(index, 'labour', e.target.value)}
                          className="w-full border-0 focus:outline-none text-sm text-right"
                          step="0.01"
                        />
                      </td>
                    </tr>
                  ))}
                  {/* Add empty rows if needed */}
                  {formData.parts.length < 10 && Array.from({ length: 10 - formData.parts.length }).map((_, index) => (
                    <tr key={`empty-${index}`}>
                      <td className="border border-black px-2 py-1 h-8"></td>
                      <td className="border border-black px-2 py-1"></td>
                      <td className="border border-black px-2 py-1"></td>
                      <td className="border border-black px-2 py-1"></td>
                      <td className="border border-black px-2 py-1"></td>
                      <td className="border border-black px-2 py-1"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary Section */}
            <div className="flex justify-end mt-4">
              <div className="w-64 space-y-2">
                <div className="flex justify-between border-2 border-black px-2 py-1">
                  <span className="font-semibold text-sm">Parts Total</span>
                  <span className="text-sm text-right font-semibold">
                    ₹{formData.parts.reduce((sum, part) => sum + (parseFloat(part.lineTotal || 0) || 0), 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between border-2 border-black px-2 py-1">
                  <span className="font-semibold text-sm">Total Labour</span>
                  <input
                    type="number"
                    name="labour"
                    value={formData.labour}
                    onChange={handleLabourChange}
                    className="w-24 border-0 focus:outline-none text-sm text-right font-semibold bg-transparent"
                    step="0.01"
                  />
                </div>
                <div className="flex justify-between border-2 border-black px-2 py-1">
                  <span className="font-semibold text-sm">Sub Total</span>
                  <input
                    type="text"
                    name="subTotal"
                    value={`₹${formData.subTotal.toFixed(2)}`}
                    readOnly
                    className="w-24 border-0 focus:outline-none text-sm text-right font-semibold bg-transparent"
                  />
                </div>
                <div className="flex justify-between border-2 border-black px-2 py-1">
                  <span className="font-semibold text-sm">VAT (18%)</span>
                  <input
                    type="text"
                    name="vat"
                    value={`₹${formData.vat.toFixed(2)}`}
                    readOnly
                    className="w-24 border-0 focus:outline-none text-sm text-right font-semibold bg-transparent"
                  />
                </div>
                <div className="flex justify-between border-2 border-black px-2 py-1 bg-gray-200">
                  <span className="font-bold text-sm">TOTAL</span>
                  <input
                    type="text"
                    name="total"
                    value={`₹${formData.total.toFixed(2)}`}
                    readOnly
                    className="w-24 border-0 focus:outline-none text-sm text-right font-bold bg-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Add Part Button - excluded from print */}
          <div className="mb-6 print:hidden no-print">
            <button
              onClick={addPartRow}
              className="btn-outline text-sm"
            >
              + Add Part Row
            </button>
          </div>

          {/* Intake + Timeline (live record) */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-black/20 rounded-lg p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Intake details</h3>
              <div className="text-sm text-gray-800 space-y-1">
                <div><span className="font-semibold">Scheduled:</span> {intake?.scheduledTime ? new Date(intake.scheduledTime).toLocaleString() : '—'}</div>
                <div><span className="font-semibold">Service mode:</span> {intake?.serviceMode || '—'}</div>
                <div><span className="font-semibold">Mechanic:</span> {intake?.assignedMechanicId || '—'}</div>
                <div><span className="font-semibold">Preliminary:</span> {intake?.preliminaryNotes || '—'}</div>
              </div>
            </div>
            <div className="border border-black/20 rounded-lg p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Timeline</h3>
              <div className="max-h-56 overflow-auto space-y-2">
                {(job?.timeline || []).length ? (
                  (job.timeline || []).slice().reverse().map((t, idx) => {
                    const timelineStatus = String(t?.status || '').trim();

                    // QC is internal: hide all QC timeline items from the customer.
                    if (['ReadyForQC', 'QCRejected', 'QCApproved'].includes(timelineStatus)) return null;

                    return (
                      <div key={idx} className="text-sm">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-semibold text-gray-900">{timelineStatus.replace(/([A-Z])/g, ' $1').trim()}</div>
                          <div className="text-xs text-gray-500">{t.at ? new Date(t.at).toLocaleString() : '—'}</div>
                        </div>
                        {t.note ? <div className="text-xs text-gray-600">{t.note}</div> : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-sm text-gray-500">No timeline yet.</div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="grid grid-cols-3 gap-6 mt-8">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Technician Name</label>
              <input
                type="text"
                name="technicianName"
                value={formData.technicianName}
                onChange={handleInputChange}
                className="w-full border-2 border-black px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Technician Signature</label>
              <input
                type="text"
                name="technicianSignature"
                value={formData.technicianSignature}
                onChange={handleInputChange}
                className="w-full border-2 border-black px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Date</label>
              <input
                type="date"
                name="technicianDate"
                value={formData.technicianDate}
                onChange={handleInputChange}
                className="w-full border-2 border-black px-2 py-1 text-sm"
              />
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 text-xs text-gray-600 italic">
            This is not an invoice, all estimates are valid for 30 days
          </div>

          {/* Copyright */}
          <div className="mt-4 text-xs text-gray-500 text-right">
            DESIGN BY BIGBOLDCREATIVE.COM © COPYRIGHT 2019
          </div>
        </div>
      </div>

      {/* Print & PDF Styles */}
      <style>{`
        /* Ensure text inside fields is fully visible */
        #jobcard-print-content input,
        #jobcard-print-content textarea {
          padding-top: 8px !important;
          padding-bottom: 8px !important;
          line-height: 1.4 !important;
        }

        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          body {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default VehicleJobCard;

